import { Phone, PhoneOff, Mic, MicOff, X, Loader2, PhoneIncoming } from "lucide-react";
import { useVoiceCall } from "@/contexts/VoiceCallProvider";

const fmt = (s) => {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
};

const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "").slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : (p || "Unknown");
};

const STATE_LABEL = {
  connecting: "Connecting…",
  ringing: "Ringing…",
  "in-call": "In call",
  ended: "Call ended",
  error: "Call failed",
};

/**
 * Incoming-call ringing panel. Shown when an inbound call is routed to this
 * browser softphone, before it's accepted.
 */
function IncomingPanel({ from, onAccept, onReject }) {
  return (
    <div
      className="fixed bottom-5 right-5 z-[121] w-[300px] rounded-2xl border border-emerald-500/30 bg-[#0d141c]/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-4 animate-fade-in"
      data-testid="incoming-call-widget"
    >
      <div className="flex items-center gap-2.5 mb-4">
        <div className="w-9 h-9 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
          <PhoneIncoming className="w-4 h-4 text-emerald-400 animate-pulse" />
        </div>
        <div className="min-w-0">
          <div className="text-[11px] font-bold uppercase tracking-widest text-emerald-400">Incoming call</div>
          <div className="text-white text-sm font-semibold truncate" data-testid="incoming-call-from">{fmtPhone(from)}</div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={onReject} data-testid="incoming-reject"
          className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-all">
          <PhoneOff className="w-4 h-4" /> Decline
        </button>
        <button onClick={onAccept} data-testid="incoming-accept"
          className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-all">
          <Phone className="w-4 h-4" /> Accept
        </button>
      </div>
    </div>
  );
}

/**
 * Floating in-call widget. Rendered once globally; visible whenever a call is
 * active (or just ended). Provides mute + hangup + a live timer.
 */
export function CallWidget() {
  const { callState, callInfo, muted, elapsed, errorMsg, hangup, toggleMute, closeWidget,
          incoming, acceptIncoming, rejectIncoming } = useVoiceCall();

  // An inbound call is ringing and not yet answered.
  if (incoming && callState !== "in-call") {
    return <IncomingPanel from={incoming.from} onAccept={acceptIncoming} onReject={rejectIncoming} />;
  }

  if (callState === "idle" || !callInfo) return null;

  const active = callState === "connecting" || callState === "ringing" || callState === "in-call";
  const ended = callState === "ended" || callState === "error";
  const isInbound = callInfo.direction === "inbound";
  const name = callInfo.contactName || (isInbound ? fmtPhone(callInfo.to) : callInfo.to);

  return (
    <div
      className="fixed bottom-5 right-5 z-[120] w-[300px] rounded-2xl border border-white/10 bg-[#0d141c]/95 backdrop-blur-xl shadow-2xl shadow-black/50 p-4 animate-fade-in"
      data-testid="call-widget"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${ended ? "bg-slate-600/30" : "bg-emerald-500/20"}`}>
            {callState === "in-call"
              ? <Phone className="w-4 h-4 text-emerald-400" />
              : callState === "error"
                ? <PhoneOff className="w-4 h-4 text-red-400" />
                : <Loader2 className={`w-4 h-4 text-emerald-400 ${active ? "animate-spin" : ""}`} />}
          </div>
          <div className="min-w-0">
            <div className="text-white text-sm font-semibold truncate" data-testid="call-widget-name">{name}</div>
            <div className="text-slate-500 text-[11px] truncate">{callInfo.to}</div>
          </div>
        </div>
        {ended && (
          <button onClick={closeWidget} data-testid="call-widget-close"
            className="text-slate-500 hover:text-white transition-colors">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <span className={`text-[11px] font-bold uppercase tracking-widest ${callState === "error" ? "text-red-400" : callState === "in-call" ? "text-emerald-400" : "text-slate-400"}`}
          data-testid="call-widget-status">
          {STATE_LABEL[callState] || ""}
        </span>
        {callState === "in-call" && (
          <span className="text-slate-300 text-xs font-mono tabular-nums" data-testid="call-widget-timer">{fmt(elapsed)}</span>
        )}
      </div>

      {errorMsg && callState === "error" && (
        <div className="text-red-300 text-[11px] mb-3 leading-snug" data-testid="call-widget-error">{errorMsg}</div>
      )}

      {active && (
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            data-testid="call-widget-mute"
            disabled={callState !== "in-call"}
            className={`flex-1 flex items-center justify-center gap-2 h-10 rounded-xl text-xs font-semibold transition-all disabled:opacity-40 ${
              muted ? "bg-amber-500/20 text-amber-400 border border-amber-500/40" : "bg-white/[0.04] text-slate-300 border border-white/10 hover:bg-white/[0.08]"
            }`}
          >
            {muted ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            {muted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={hangup}
            data-testid="call-widget-hangup"
            className="flex-1 flex items-center justify-center gap-2 h-10 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-bold transition-all"
          >
            <PhoneOff className="w-4 h-4" /> Hang up
          </button>
        </div>
      )}
    </div>
  );
}
